import { InputWithLabel } from '@adotel/components'
import {
  Button,
  ButtonBase,
  CircularProgress,
  Grow,
  Hidden,
  MenuItem,
  MenuList,
  Modal,
  Paper,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import { matchSorter } from 'match-sorter'
import React, { ReactNode, useEffect, useRef, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroller'

import { getDirection, getTranslate } from '../../localization'
import useStyles from './styles/AutocompleteForFilter.styles'

interface AutocompleteForFilterProps {
  value: { label: string; value: string }[]
  onClick: (item: any) => void
  size?: 'small' | 'medium'
  options: { label: string; value: string }[]
  Icon: ReactNode
  buttonPlaceholder?: string
  fullwidth?: boolean
  hidePlacehoder?: boolean
  loading: boolean
  showOptionsSorted?: boolean
  isAddIcon?: boolean
  noBorder?: boolean
  fixPositionForBulkUpdate?: boolean
  setlistIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  setmodalForMobileIsOpen: React.Dispatch<React.SetStateAction<boolean>>
  listIsOpen: boolean
  setgrowIn: React.Dispatch<React.SetStateAction<boolean>>
  growIn: boolean
  modalForMobile: boolean
}

const ITEMS_PER_LOAD = 12

const Index: React.FC<AutocompleteForFilterProps> = ({
  onClick,
  value,
  size,
  options,
  Icon,
  buttonPlaceholder,
  hidePlacehoder = false,
  fullwidth,
  loading,
  showOptionsSorted,
  isAddIcon = false,
  noBorder = false,
  fixPositionForBulkUpdate = false,
  setlistIsOpen,
  listIsOpen,
  setgrowIn,
  growIn,
  modalForMobile,
  setmodalForMobileIsOpen
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const [searchInput, setsearchInput] = useState('')
  const [filteredOptions, setfilteredOptions] = useState<
    { label: string; value: string }[]
  >([])
  const [lastLoadeditemIndex, setlastLoadeditemIndex] = useState(ITEMS_PER_LOAD)

  const handleToggleList = () => {
    if (growIn) {
      setTimeout(() => {
        setsearchInput('')
        setlistIsOpen(false)
        setmodalForMobileIsOpen(false)
      }, 260)
    } else {
      setlistIsOpen(true)
      setmodalForMobileIsOpen(true)
    }
    setgrowIn(!growIn)
  }

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        !isSmall &&
        wrapperRef.current &&
        !wrapperRef?.current?.contains(event.target as Node) &&
        growIn
      ) {
        handleToggleList()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [wrapperRef, growIn])

  useEffect(() => {
    const newFilteredList = matchSorter(options, searchInput, {
      keys: ['label', 'value'],
      sorter: (matchedItems) =>
        matchedItems.sort((a, b) =>
          Array.isArray(value) &&
          value.some((val) => val.label === b.item.label)
            ? 1
            : Array.isArray(value) &&
              value.some((val) => val.label === a.item.label)
            ? -1
            : showOptionsSorted
            ? a.item.label < b.item.label
              ? -1
              : 1
            : 1
        )
    })
    setfilteredOptions(newFilteredList)
  }, [searchInput, options])
  const handleLoadMore = () => {
    setlastLoadeditemIndex((currentValue) => currentValue + ITEMS_PER_LOAD)
  }

  return (
    <div
      ref={wrapperRef}
      className={`${classes.root} ${fullwidth ? classes.fullwidth : ''} ${
        isAddIcon ? classes.isAddIcon : noBorder ? classes.noBorder : ''
      }`}>
      <ButtonBase
        className={listIsOpen ? classes.buttonOnShow : classes.button}
        onClick={handleToggleList}>
        <div
          className={[
            classes.icon,
            hidePlacehoder ? classes.iconWithNoMargin : ''
          ].join(' ')}>
          {loading ? <CircularProgress size={14} /> : Icon}
        </div>
        {!hidePlacehoder && (
          <Typography className={classes.label}>{buttonPlaceholder}</Typography>
        )}
      </ButtonBase>
      {listIsOpen && (
        <Hidden smDown>
          <Grow in={growIn} timeout={250}>
            <Paper
              elevation={4}
              className={[
                classes.filterSelectorPaper,
                fixPositionForBulkUpdate
                  ? classes.filterSelectorPaperForBulkUpdate
                  : ''
              ].join(' ')}>
              <div className={classes.formsContainer}>
                <div className={classes.input}>
                  <InputWithLabel
                    startIcon={<SearchIcon />}
                    noHelperText
                    value={searchInput}
                    placeholder={
                      !fixPositionForBulkUpdate
                        ? getTranslate('Search in Filters')
                        : getTranslate('Search in Policies')
                    }
                    onChange={(event) =>
                      setsearchInput(event.currentTarget.value)
                    }
                    inputSize={size || 'small'}
                  />
                </div>
                <div
                  className={classes.itemsContainer}
                  onClick={() => setlistIsOpen(false)}>
                  <InfiniteScroll
                    pageStart={0}
                    loadMore={handleLoadMore}
                    hasMore={lastLoadeditemIndex <= filteredOptions.length - 1}
                    loader={
                      <div className="loader" key={0}>
                        Loading ...
                      </div>
                    }
                    useWindow={false}>
                    <MenuList>
                      {filteredOptions
                        .slice(0, lastLoadeditemIndex)
                        .map((item) => (
                          <MenuItem
                            key={item.label + item.value}
                            className={[
                              classes.menuItem,
                              value &&
                              value.some((val) => val.label === item.label)
                                ? classes.activeMenuItem
                                : ''
                            ].join(' ')}
                            component={Button}
                            fullWidth
                            value={item.value}
                            onClick={() => {
                              onClick(item)
                            }}>
                            {item.label}
                          </MenuItem>
                        ))}
                    </MenuList>
                  </InfiniteScroll>
                </div>
              </div>
              <Button
                onClick={() => setlistIsOpen(false)}
                variant="contained"
                color="primary"
                className={classes.doneButton}>
                {getTranslate('Done')}
              </Button>
            </Paper>
          </Grow>
        </Hidden>
      )}
      {modalForMobile && (
        <Hidden mdUp>
          <Modal
            dir={getDirection()}
            open={growIn}
            onClose={() => setmodalForMobileIsOpen(false)}
            onClick={() => setlistIsOpen(false)}
            className={classes.centerModal}>
            <Paper elevation={4} className={classes.centerModal}>
              <div className={classes.formsContainer}>
                <div className={classes.input}>
                  <InputWithLabel
                    startIcon={<SearchIcon />}
                    noHelperText
                    value={searchInput}
                    placeholder={
                      !fixPositionForBulkUpdate
                        ? getTranslate('Search in Filters')
                        : getTranslate('Search in Policies')
                    }
                    onChange={(event) =>
                      setsearchInput(event.currentTarget.value)
                    }
                    inputSize={size || 'small'}
                  />
                </div>
                <div className={classes.itemsContainer}>
                  <InfiniteScroll
                    pageStart={0}
                    loadMore={handleLoadMore}
                    hasMore={lastLoadeditemIndex <= filteredOptions.length - 1}
                    loader={
                      <div className="loader" key={0}>
                        Loading ...
                      </div>
                    }
                    useWindow={false}>
                    <MenuList>
                      {filteredOptions
                        .slice(0, lastLoadeditemIndex)
                        .map((item) => (
                          <MenuItem
                            key={item.label}
                            className={[
                              classes.menuItem,
                              value &&
                              value.some((val) => val.label === item.label)
                                ? classes.activeMenuItem
                                : ''
                            ].join(' ')}
                            component={Button}
                            fullWidth
                            value={item.value}
                            onClick={() => {
                              onClick(item)
                            }}>
                            {item.label}
                          </MenuItem>
                        ))}
                    </MenuList>
                  </InfiniteScroll>
                </div>
              </div>
              <Button
                onClick={() => {
                  setmodalForMobileIsOpen(false)
                }}
                variant="contained"
                color="primary"
                className={classes.doneButton}>
                {getTranslate('Done')}
              </Button>
            </Paper>
          </Modal>
        </Hidden>
      )}
    </div>
  )
}

export default Index
